import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';

const ExamUnionExamsList = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();

  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);

  // ==================== FETCH DATA ====================
  const fetchExamMasters = useCallback(() => {
    const config = {
      url: `/exammaster/getexamsfortargetcode?targetCode=ExamUnion`,
      method: "get",
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response)) {
          setExams(response);
        } else if (response && Array.isArray(response.exams)) {
          setExams(response.exams);
        }
      })
      .catch(console.error);
  }, [authState.token, fetchData]);

  useEffect(() => {
    fetchExamMasters();
  }, [fetchExamMasters]);

  // ==================== SORT BY TITLE ====================
const sortedExams = exams
  ? [...exams].sort((a, b) => {
      // First sort by displaySN if present, otherwise treat as 0
      const snA = a.displaySN ?? 0;
      const snB = b.displaySN ?? 0;
      if (snA !== snB) return snA - snB;

      // If displaySN is equal or missing, sort alphabetically by examTitle
      return a.examTitle.localeCompare(b.examTitle);
    }) : [];

  const examFields = [
    { label: "Category", key: "category"},
    { label: "State", key: "state"},
    { label: "Country", key: "country"},
    { label: "Who Can Appear", key: "whoCanAppear"},
    { label: "Subjects", key: "subjects"},
    { label: "Exam Pattern", key: "examPattern"},
    { label: "Passing Criteria", key: "passingCriteria"},
    { label: "Attempts", key: "attempts"},
    { label: "Difficulty Level", key: "difficultyLevel"},
    { label: "What After Passing", key: "whatAfterPassing"},
    { label: "Key Insights", key: "keyInsights"},
    { label: "ExamUnion Remarks", key: "examUnionRemarks"},
    // { label: "Session", key: "examSession"},
    // { label: "Branch", key: "examBranch"},
    // { label: "Batch", key: "examBatch"},
    // { label: "Sub Batch", key: "examSubBatch"},
    // { label: "Teacher", key: "examTeacher"},
    // { label: "Group", key: "examGroup"},
    // { label: "Student", key: "examStudent"},
];

  return (
    <div className="p-4 bg-gradient-to-b from-gray-500 via-gray-700 to-gray-900 min-h-screen">
      <h1 className="text-lg lg:text-xl font-bold text-yellow-200 text-center mb-2">
        ExamUnion List of Exams
      </h1>
        <p className="text-center text-gray-100 text-xs lg:text-sm mb-2 px-4">
            Explore all the top competitive and academic exams in one place! Click on any exam below to see details, tips, books, and history – all curated for easy access and mobile-first experience.
        </p>

        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-400 rounded-xl shadow-lg p-2 mb-2">
            <p className="text-center text-white text-xs lg:text-sm font-semibold tracking-wide">
                <span className="font-bold">ExamUnion </span>— Built for fighters who refuses to settle.
            </p>
            <p className="text-center text-white text-xs lg:text-sm font-semibold tracking-wide">
                <span className='text-gray-100 font-bold'>Foundation to Examdays</span> - Global Self & Group Test Series All-in-one battle arena.
            </p>
            <p className="text-center text-white text-xs lg:text-sm font-semibold tracking-wide">
                Join thousands of students, explore top exams, plan your path, and level up your career!
            </p>
        </div>    

        <div className="grid grid-cols-4 lg:grid-cols-8 gap-1 p-4">
            {sortedExams.map((exam, idx) => (
                <div
                key={exam._id}
                className="bg-purple-700 hover:bg-purple-600 transition-colors duration-300 rounded-lg p-4 cursor-pointer flex items-center justify-center"
                >
                <h2 className="text-white text-xs font-semibold text-center">
                    {exam.examTitle}
                </h2>
                </div>
            ))}
        </div>

      <ul className="space-y-3">
        {sortedExams.map((exam, idx) => (
          <li
            key={exam._id}
            className="bg-white rounded-2xl shadow-md p-1 lg:p-2 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            onClick={() => setSelectedExam(selectedExam?._id === exam._id ? null : exam)}
          >
            {/* Exam Row */}
            <div className="flex justify-between items-center">
              <h2 className="text-xs lg:text-sm font-semibold text-purple-700">
                {idx + 1}. {exam.examTitle}
              </h2>
              <span className="text-xs lg:text-sm text-gray-500 px-2 py-1 bg-purple-100 rounded-full">
                {exam.examType}
              </span>
            </div>

            {/* Expand Details */}
            {selectedExam?._id === exam._id && (
              <div className="mt-4 text-gray-700 space-y-3">
                <p className='text-xs lg:text-sm'><strong>Overview:</strong> {exam.examOverview}</p>
                <p className='text-xs lg:text-sm'><strong>Authority:</strong> {exam.examAuthority}</p>
                <p className='text-xs lg:text-sm'><strong>Institution:</strong> {exam.examInstitution}</p>

                {examFields
                  .filter(field => {
                    const value = exam?.[field.key];
                    return value !== undefined && value !== null && value !== "";
                  })
                  .map((field, idx) => (
                    <p key={idx} className="text-xs lg:text-sm">
                      <strong>{field.label}:</strong> {exam?.[field.key]}
                    </p>
                ))}

                {exam.examHistory.length > 0 && (
                  <div className="p-3 border-l-4 border-purple-400 bg-purple-50 rounded-md space-y-1">
                    <h3 className="font-semibold text-purple-600">Exam History</h3>
                    {exam.examHistory.map((h, idx) => (
                      <div key={idx} className="text-sm">
                        <p className='text-xs lg:text-sm'><strong>Registration:</strong> {new Date(h.registrationStartDate).toLocaleDateString()} - {new Date(h.registrationEndDate).toLocaleDateString()}</p>
                        <p className='text-xs lg:text-sm'><strong>Exam Days:</strong> {h.examDays}</p>
                        <p className='text-xs lg:text-sm'><strong>Vacancy:</strong> {h.vacancy}</p>
                        <p className='text-xs lg:text-sm'><strong>Eligibility:</strong> {h.eligibility}</p>
                        <p className='text-xs lg:text-sm'><strong>Syllabus:</strong> {h.syllabus}</p>
                      </div>
                    ))}
                  </div>
                )}

                {exam.books.length > 0 && (
                  <div className="p-3 border-l-4 border-green-400 bg-green-50 rounded-md space-y-1">
                    <h3 className="font-semibold text-green-600">Books</h3>
                    {exam.books.map((b, idx) => (
                      <div key={idx} className="text-sm">
                        <p className='text-xs lg:text-sm'><strong>Title:</strong> {b.title}</p>
                        <p className='text-xs lg:text-sm'><strong>Author:</strong> {b.author}</p>
                        <p className='text-xs lg:text-sm'><strong>Price:</strong> {b.price} {b.currency}</p>
                      </div>
                    ))}
                  </div>
                )}

                {exam.tips.length > 0 && (
                  <div className="p-3 border-l-4 border-yellow-400 bg-yellow-50 rounded-md space-y-1">
                    <h3 className="font-semibold text-yellow-700">Tips</h3>
                    {exam.tips.map((tip, idx) => (
                      <p key={idx} className="text-sm">"{tip.tipText}" - {tip.tipBy}</p>
                    ))}
                  </div>
                )}

                {exam.studentTestimonials.length > 0 && (
                  <div className="p-3 border-l-4 border-pink-400 bg-pink-50 rounded-md space-y-1">
                    <h3 className="font-semibold text-pink-700">Student Testimonials</h3>
                    {exam.studentTestimonials.map((testi, idx) => (
                      <div key={idx} className="text-sm">
                        <p className='text-xs lg:text-sm'>"{testi.textTestimonial}" - {testi.studentName}</p>
                        {testi.videoTestimonialLink && (
                          <a
                            href={testi.videoTestimonialLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-purple-600 underline text-sm"
                          >
                            Watch Video
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
        <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
            <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
                <div className="flex flex-wrap gap-4 text-sm">
                    <Link to="/about-us" className="hover:text-teal-300">About</Link>
                    <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                    <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                    <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
                </div>
            </div>
        </footer>      
    </div>
  );
};

export default ExamUnionExamsList;